import React from 'react';
import type { ClientFollowUpDto, ClientFollowUpKind } from '../../lib/confirmedOrderApi';
import { formatDateTime } from '../../utils/dateFormat';
import { emailLineFor, emailLinesNotOnOrder } from './reconcileLines';
import { OrderReadIssuesNote } from './OrderReadIssuesNote';

const KIND_LABEL: Record<ClientFollowUpKind, string> = {
  revised_po: 'A revised PO',
  change_request: 'A change to the order',
  cancellation: 'A cancellation',
  other: 'Another email about this order',
};

interface Props {
  followUp: ClientFollowUpDto;
  /** The order's lines as they are now. */
  orderLines: any[];
  onApply: (followUp: ClientFollowUpDto) => void;
  onDismiss: (followUp: ClientFollowUpDto) => void;
  busy?: boolean;
  error?: string | null;
}

export const qtyText = (quantity: unknown, uom: unknown) =>
  quantity === null || quantity === undefined || quantity === '' ? '—' : `${quantity}${uom ? ` ${uom}` : ''}`;

/** The uploader's name when the detail read loaded it (getConfirmedOrderDetail), else '' — never an id. */
const uploaderName = (uploadedBy: ClientFollowUpDto['uploadedBy']): string =>
  uploadedBy && typeof uploadedBy === 'object'
    ? [uploadedBy.firstName, uploadedBy.lastName].filter(Boolean).join(' ')
    : '';

/**
 * D45.3, D181: a later email about a confirmed order, or a change in the email that confirmed it,
 * never changes the order. It waits here, with the email's lines beside the order's, until a person
 * applies it (Review & Finalise, seeded with the email's lines) or dismisses it.
 */
export const ClientFollowUpPanel: React.FC<Props> = ({ followUp, orderLines, onApply, onDismiss, busy = false, error }) => {
  const extracted = followUp.extractedItems || [];
  const notOnOrder = emailLinesNotOnOrder(orderLines, extracted);
  const when = formatDateTime(followUp.receivedAt);
  // D81 rule 5 — an uploaded ship's PO has no email and no sender: it is named by its file.
  const uploaded = followUp.source === 'upload';
  const title = uploaded ? "Ship's PO uploaded" : 'Client email about this order';
  const sourceNoun = uploaded ? "the ship's PO" : 'the email';
  const uploader = uploaderName(followUp.uploadedBy);
  const origin = uploaded
    ? `The ship's PO ${followUp.file?.filename || ''}, uploaded${uploader ? ` by ${uploader}` : ''}`
    : `${KIND_LABEL[followUp.kind] || KIND_LABEL.other}${followUp.from ? ` from ${followUp.from}` : ''}`;

  return (
    <section
      aria-label={title}
      className="p-4 border-b border-amber-200 bg-amber-50 space-y-3"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-amber-800">{title} — check it</div>
          <div className="text-xs text-amber-700">
            {origin}
            {when ? ` · ${when}` : ''}
          </div>
          {followUp.subject && <div className="text-xs text-amber-700 truncate">Subject: {followUp.subject}</div>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => onApply(followUp)}
            disabled={busy}
            className="px-4 py-2 rounded-md bg-amber-600 text-white text-xs font-semibold hover:bg-amber-700 disabled:opacity-60"
          >
            Apply
          </button>
          <button
            type="button"
            onClick={() => onDismiss(followUp)}
            disabled={busy}
            className="px-4 py-2 rounded-md border border-amber-300 bg-white text-amber-900 text-xs font-semibold hover:bg-amber-100 disabled:opacity-60"
          >
            Dismiss
          </button>
        </div>
      </div>

      {followUp.requestedChanges?.length > 0 && (
        <div className="text-xs text-amber-900">
          <div className="font-semibold">The client asks:</div>
          <ul className="mt-0.5 list-disc pl-4 space-y-0.5">
            {followUp.requestedChanges.map((sentence, i) => (
              <li key={i}>“{sentence}”</li>
            ))}
          </ul>
        </div>
      )}

      {followUp.kind === 'cancellation' && (
        <div className="text-xs text-amber-900">Apply changes the order’s lines only; it does not cancel the order.</div>
      )}

      <OrderReadIssuesNote issues={followUp.readIssues} heading={`Not everything in ${sourceNoun} was read:`} />

      {extracted.length > 0 && (
        <div className="overflow-auto rounded-md border border-amber-200 bg-white">
          <table className="data-table-grid w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-3 py-2 text-left">#</th>
                <th className="px-3 py-2 text-left">Item</th>
                <th className="px-3 py-2 text-right">On the order now</th>
                <th className="px-3 py-2 text-right">{uploaded ? "In the ship's PO" : 'In the email'}</th>
              </tr>
            </thead>
            <tbody>
              {orderLines.map((line, index) => {
                const match = emailLineFor(line, index, extracted);
                const changed = match !== undefined && match.quantity !== line.quantity;
                return (
                  <tr key={index} className={`border-b border-slate-100 ${changed ? 'bg-amber-50' : ''}`}>
                    <td className="px-3 py-2 text-slate-400">{line.lineNumber ?? index + 1}</td>
                    <td className="px-3 py-2 text-slate-800">{line.itemName || line.itemDescription || '--'}</td>
                    <td className="px-3 py-2 text-right text-slate-600">{qtyText(line.quantity, line.uom)}</td>
                    <td className={`px-3 py-2 text-right ${changed ? 'font-semibold text-amber-700' : 'text-slate-600'}`}>
                      {match ? qtyText(match.quantity, match.uom) : `not in ${sourceNoun}`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {notOnOrder.length > 0 && (
        <div className="text-xs text-amber-900">
          <div className="font-semibold">In {sourceNoun} but not on the order (Apply does not add these):</div>
          <ul className="mt-0.5 list-disc pl-4 space-y-0.5">
            {notOnOrder.map((item, i) => (
              <li key={item._id || i}>
                {item.itemName} — {qtyText(item.quantity, item.uom)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {error && (
        <div role="alert" className="text-xs text-rose-700">
          {error}
        </div>
      )}
    </section>
  );
};
